import { Component, HostListener, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import {
  FormGroup,
  AbstractControl,
  FormBuilder,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { TranslationService } from 'src/app/shared/data/translation.service';
import { AuthService } from '../data/auth.service';
import { pincodeValidator } from '../util/auth.util';
import { TranslatePipe } from 'src/app/shared/data/pipes/translate.pipe';
import { ButtonComponent } from 'src/app/shared/ui-common/button/button.component';
import { PhoneInputComponent } from 'src/app/shared/ui-common/phone-input/phone-input.component';
import { AuthLayoutComponent } from '../ui/auth-layout/auth-layout.component';
import { PinCodeComponent } from '../ui/pin-code/pin-code.component';
import {
  DemoSignUpPayload,
  DemoSignUp,
} from '../data/models/demo-sign-up.model';
import { EnvironmentService } from 'src/app/shared/data/environment.service';

enum Step {
  Welcome = 'welcome',
  Pincode = 'pincode',
  ConfirmPincode = 'confirm-pincode',
  Consent = 'consent',
  PersonalDetails = 'personal-details',
  SsoLinking = 'sso-linking',
  Done = 'done',
}

@Component({
  selector: 'app-feature-sign-up-demo',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AuthLayoutComponent,
    ButtonComponent,
    PinCodeComponent,
    TranslatePipe,
    PhoneInputComponent,
  ],
  templateUrl: './feature-sign-up-demo.component.html',
  styleUrls: [
    './feature-sign-up-demo.component.scss',
    '../ui/auth.styles.scss',
  ],
})
export class FeatureSignUpDemoComponent implements OnInit {
  signUpForm: FormGroup;
  signUpData!: DemoSignUp;
  companySignUpToken: string | null = null;

  dropdownOpen = false;
  steps = Step;
  isTransitioning = false;
  step = signal<Step>(Step.Welcome);
  pincodeNotMatched = signal<boolean>(false);
  signUpValidationError = signal<string>('');
  companyName = signal<string>('');
  departmentDropdownOpen = false;

  get errorMessage(): string {
    if (
      this.signUpForm.controls['pincode'].errors &&
      this.signUpForm.controls['pincode'].touched
    ) {
      if (this.signUpForm.controls['pincode'].errors['invalidPincode']) {
        return 'WebApp.v1.Auth.ForgotCode.Error.Not4Digit';
      } else if (
        this.signUpForm.controls['pincode'].errors['repeatingDigits']
      ) {
        return 'WebApp.v1.Auth.ForgotCode.Error.RepeatingDigits';
      } else if (
        this.signUpForm.controls['pincode'].errors['sequentialDigits']
      ) {
        return 'WebApp.v1.Auth.ForgotCode.Error.NoSequentialDigits';
      }
    } else if (this.pincodeNotMatched()) {
      return 'WebApp.v1.Auth.ForgotCode.Error.NotMatched';
    }

    return '';
  }

  get pincodeControl(): AbstractControl<string> | null {
    return this.signUpForm.get('pincode');
  }

  get terms(): string | undefined {
    return this.signUpData?.terms;
  }

  get showZipCode(): boolean {
    return this.signUpData?.showZipCode === true;
  }

  get showAddress(): boolean {
    return this.signUpData?.showAddress === true;
  }

  get showHomeCity(): boolean {
    return this.signUpData?.showHomeCity === true;
  }

  get showBirthday(): boolean {
    return this.signUpData?.showBirthday === true;
  }

  get showDepartmentDropdown(): boolean {
    return this.signUpData?.showDepartmentDropdown === true
      && !!this.signUpData.departmentDropdownOptions?.length;
  }

  get departmentDropdownOptions(): string[] {
    return this.signUpData?.departmentDropdownOptions ?? [];
  }

  get selectedDepartmentLabel(): string {
    return this.signUpForm.get('department')?.value
      || 'WebApp.v1.Auth.SignUp.PersonalDetails.Form.Department';
  }

  get isCompanySpecific(): boolean {
    return !!this.companySignUpToken?.length;
  }

  get isSso(): boolean {
    return !!this.signUpData?.isSsoEnabled;
  }

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private authService: AuthService,
    private router: Router,
    private translationService: TranslationService,
    private environmentService: EnvironmentService
  ) {
    this.signUpForm = this.formBuilder.group({
      pincode: ['', [Validators.required, pincodeValidator()]],
      firstname: ['', Validators.required],
      lastname: ['', Validators.required],
      company: ['', Validators.required],
      department: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.pattern(/^\d{6}$/)]],
      zipCode: [''],
      homeCity: [''],
      homeAddress: [''],
      birthday: [''],
      immediateManager: [''],
    });
  }

  async ngOnInit(): Promise<void> {
    const language = this.route.snapshot.queryParamMap.get('language');
    const partner = this.route.snapshot.queryParamMap.get('partner');
    this.companySignUpToken = this.getCompanySignUpToken();

    if (this.companySignUpToken) {
      this.signUpData = await this.authService.getCompanySignUpData(
        this.companySignUpToken,
        language ?? 'en'
      );
    } else {
      this.signUpData = await this.authService.getDemoSignUpData(
        language ?? 'en',
        partner ?? null
      );
    }

    this.translationService.setAuthLanguage(this.signUpData.languageId ?? 1045);
    this.onPatchFormValue();
  }

  onPatchFormValue(): void {
    this.companyName.set(this.signUpData.companyName ?? '');
    this.signUpForm.patchValue({
      company: this.signUpData.companyName ?? '',
    });
  }

  @HostListener('document:click')
  closeDepartmentDropdown(): void {
    this.departmentDropdownOpen = false;
  }

  toggleDepartmentDropdown(): void {
    this.departmentDropdownOpen = !this.departmentDropdownOpen;
  }

  selectDepartment(department: string): void {
    const departmentControl = this.signUpForm.get('department');

    departmentControl?.setValue(department);
    departmentControl?.markAsTouched();
    departmentControl?.updateValueAndValidity();
    this.departmentDropdownOpen = false;
  }

  async goToNextStep(step: Step): Promise<void> {
    this.isTransitioning = true;

    setTimeout(() => {
      this.step.set(step);
      this.isTransitioning = false;
    }, 400);
  }

  updatePincode(pin: string): void {
    if (this.pincodeControl?.errors) {
      this.pincodeControl.setValue('');
      this.pincodeControl.setErrors(null);
      this.pincodeControl.updateValueAndValidity({ onlySelf: true });
    }

    if (pin.length === 4) {
      this.submitPincode(pin);
    }
  }

  submitPincode(pin: string): void {
    if (this.step() === 'pincode') {
      this.signUpForm.get('pincode')?.setValue(pin);

      if (this.signUpForm.controls['pincode'].errors) {
        this.pincodeControl?.updateValueAndValidity({ onlySelf: true });
        this.pincodeControl?.markAsTouched();

        return;
      }

      this.goToNextStep(Step.ConfirmPincode);
    } else if (this.step() === 'confirm-pincode') {
      if (this.signUpForm.get('pincode')?.value === pin) {
        this.goToNextStep(Step.Consent);
      } else {
        this.pincodeNotMatched.set(true);
      }
    }
  }

  goToPincode(): void {
    this.goToNextStep(Step.Pincode);
    this.pincodeNotMatched.set(false);
    this.signUpForm.get('pincode')?.setValue('');
    this.signUpForm.get('pincode')?.markAsUntouched();
  }

  async completeSignup(): Promise<void> {
    const phone = this.signUpForm.get('phone')?.value ?? '';
    const phoneNumber = phone.replace(/\s+/g, '');

    const model: DemoSignUpPayload = {
      partner: this.signUpData.partner,
      firstname: this.signUpForm.get('firstname')?.value,
      lastname: this.signUpForm.get('lastname')?.value,
      company: this.signUpForm.get('company')?.value,
      department: this.signUpForm.get('department')?.value,
      email: this.signUpForm.get('email')?.value,
      mobil: phoneNumber,
      pin: this.signUpForm.get('pincode')?.value,
      pin2: this.signUpForm.get('pincode')?.value,
      zipCode: this.signUpForm.get('zipCode')?.value,
      homeCity: this.signUpForm.get('homeCity')?.value,
      homeAddress: this.signUpForm.get('homeAddress')?.value,
      birthday: this.formatBirthday(this.signUpForm.get('birthday')?.value),
      immediateManager: this.signUpForm.get('immediateManager')?.value,
    };

    try {
      let statusCode;
      const language = this.signUpLanguageCode();

      if (this.companySignUpToken) {
        statusCode = await this.authService.postCompanySignUpData(
          model,
          this.companySignUpToken,
          language
        );
      } else {
        statusCode = await this.authService.postDemoSignUpData(model, language);
      }

    if (statusCode === HttpStatusCode.Ok && this.isSso) {
      this.goToNextStep(Step.SsoLinking);

      return;
    }

      const jwtToken = await lastValueFrom(
        this.authService.onLogin(
          model.email,
          this.signUpForm.get('pincode')?.value
        )
      );

      this.authService.setCookie(jwtToken, false);
      await this.translationService.refreshTranslationsFromApi(`${this.signUpData.languageId ?? 1045}`);
      this.router.navigate(['/available-surveys']);
    } catch (errorResponse) {
      if (errorResponse instanceof HttpErrorResponse) {
        const errors = errorResponse?.error?.errors;

        if (errors) {
          const firstKey = Object.keys(errors)[0];
          this.signUpValidationError.set(errors[firstKey][0]);
        } else {
          this.signUpValidationError.set(
            'WebApp.v1.Auth.SignUp.Error.SomethingWentWrong'
          );
        }
      }
    }
  }

  continueToSsoPage(): void {
    const ssoUrl = this.environmentService.getEnvironment().ssoUrl;
    const redirectUrl = `${window.location.origin}/login`;

    localStorage.setItem('sso-email', this.signUpForm.get('email')?.value ?? '');
    window.location.href = `${ssoUrl}?redirect=${redirectUrl}`;
  }

  private getCompanySignUpToken(): string | null {
    return this.route.snapshot.paramMap.get('token')
      ?? this.route.snapshot.queryParamMap.get('token');
  }

  private formatBirthday(value?: string | null): string {
    if (!value) {
      return '';
    }

    const [year, month, day] = value.split('-');

    if (!year || !month || !day) {
      return value;
    }

    return `${day}-${month}-${year}`;
  }

  private signUpLanguageCode(): string {
    switch (this.signUpData.languageId) {
      case 1045:
        return 'en';
      case 1041:
        return 'da';
      case 1153:
        return 'se';
      default:
        return 'en';
    }
  }
}
